import React from "react";
import { Building2, ShieldCheck, FileCheck, ExternalLink } from "lucide-react";

/**
 * Verifiable institutional trust scaffolding for above-the-fold.
 * Every claim links to an external source the visitor can independently verify.
 * No photos, no testimonials — just provable facts.
 */
export const TrustStrip = () => (
  <section className="border-y border-white/10 bg-slate-900/40" data-testid="trust-strip">
    <div className="mx-auto max-w-7xl px-6 py-10">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
          Verifiable Business Credentials
        </p>
        <p className="mt-2 text-sm text-slate-400 max-w-2xl mx-auto">
          We're a registered US business. Every claim below is independently verifiable through public records.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Hawaii LLC verification */}
        <a
          href="https://hbe.ehawaii.gov/documents/search.html?queryInput=8eight8+LLC"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-start gap-4 p-5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-blue-500/40 transition"
          data-testid="trust-llc-link"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white flex items-center gap-1.5">
              Registered Hawaii LLC
              <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-blue-400 transition" />
            </p>
            <p className="text-xs text-slate-400 mt-1">
              8eight8 LLC · Honolulu, HI
            </p>
            <p className="text-xs text-slate-500 mt-1.5">
              Search the Hawaii state business registry →
            </p>
          </div>
        </a>

        {/* Process verification — link to /process */}
        <a
          href="/process"
          className="group flex items-start gap-4 p-5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-blue-500/40 transition"
          data-testid="trust-process-link"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              Transparent Case Workflow
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Track every case at 9 status stages
            </p>
            <p className="text-xs text-slate-500 mt-1.5">
              See how we work, end-to-end →
            </p>
          </div>
        </a>

        {/* Trust page — full business verification */}
        <a
          href="/trust"
          className="group flex items-start gap-4 p-5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-blue-500/40 transition"
          data-testid="trust-page-link"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              Full Business Verification
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Phone, address, refund policy, terms
            </p>
            <p className="text-xs text-slate-500 mt-1.5">
              Verify our credentials →
            </p>
          </div>
        </a>
      </div>

      <p className="mt-6 text-center text-xs text-slate-500">
        Founded 2024 in Hawaii, USA · 808-301-2134 · intake@socialdisputeco.com · ⚠ We will never ask for your password or login codes
      </p>
    </div>
  </section>
);
